<template>
    <v-container grid-list-xs>
        <v-row>
            <v-col>
                <v-sheet class="mt-10 pa-5" elevation="3">
                    <h3 id="pay_monthly-decision-header" class="mb-2 primary--text">
                        {{ $t(`pay_monthly.decision.${decision}.header`) }}
                    </h3>
                    <div id="pay_monthly-decision-body" class="mb-4 mt-2">
                        {{ $t(`pay_monthly.decision.${decision}.body-1`) }}
                    </div>
                    <div id="result-container" :class="resultContainerClass">
                        <div v-if="fraud" class="exclamation" aria-hidden="true">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path
                                    d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22
                                6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
                                    fill="#CF2A2A"
                                />
                            </svg>
                        </div>
                        <div :class="insideContainerClass">
                            <div id="pay_monthly-app-title" class="primary-color">
                                {{ $t(`pay_monthly.decision.${decision}.app-title`) }}
                            </div>
                            <div id="pay_monthly-app-body" class="font-weight-bold">
                                {{ $t(`pay_monthly.decision.${decision}.app-body`) }}
                            </div>
                            <div v-if="applyResponse" id="pay_monthly-app-number" class="primary-color">
                                #{{ applyResponse.applicationId }}
                            </div>
                        </div>
                    </div>

                    <div id="pay_monthly-decision-footer" class="mt-2">
                        {{ $t(`pay_monthly.decision.${decision}.footer`) }}
                    </div>
                    <v-row justify="center">
                        <v-col class="mt-6 d-flex justify-end">
                            <SubmitButton class="secondary" :submitting="submitting" @click="next">
                                <span> Continue To Apply </span>
                            </SubmitButton>
                        </v-col>
                    </v-row>
                </v-sheet>
            </v-col>
        </v-row>
    </v-container>
</template>

<script lang="ts">
    import { defineComponent } from "vue"
    import AutoFinishMixin from "@/components/apply/AutoFinishMixin"
    import ApplyResponseCode from "@/model/apply/ApplyResponseCode"
    import type { PendingReason } from "@/model/apply/PendingReason"
    import { providerState, sessionState } from "@/state"
    import { goto } from "@/util/navigation"

    const FraudPendingReason = "fraud" as PendingReason

    export default defineComponent({
        mixins: [AutoFinishMixin],

        data() {
            return {
                applyResponse: providerState.applyResponse,
                submitting: false,
            }
        },

        computed: {
            decisionType() {
                const hasFraudPendingReason = !!this.applyResponse?.pendingReasons?.some(
                    reason => reason === FraudPendingReason
                )

                if (this.applyResponse?.responseCode === ApplyResponseCode.PENDING) {
                    return hasFraudPendingReason ? "fraud" : "pending"
                }

                return this.applyResponse?.responseCode
            },

            pending(): boolean {
                return this.decisionType === "pending"
            },

            fraud(): boolean {
                return this.decisionType === "fraud"
            },

            decision() {
                return this.decisionType
            },

            resultContainerClass(): string {
                if (this.pending) {
                    return "no-box"
                }
                if (this.fraud) {
                    return "fraud-box"
                }
                return ""
            },

            insideContainerClass(): string {
                if (this.pending) {
                    return "no-box-inside"
                }
                if (this.fraud) {
                    return "fraud-box-inside"
                }
                return ""
            },
        },

        methods: {
            async next(): Promise<void> {
                try {
                    this.submitting = true
                    await goto({
                        path: `/providers/${sessionState.providerKey}/payMonthlyLoader`,
                        query: {
                            refreshPendingStatus: true
                        },
                    })
                } finally {
                    this.submitting = false
                }
            },
        },
    })
</script>

<style lang="scss" scoped>
    #pay_monthly-app-number {
        font-size: 26px;
        font-weight: 700;
    }

    #pay_monthly-app-title {
        font-size: 16px;
        line-height: 24px;
        font-weight: 700;
    }

    #pay_monthly-app-body {
        font-size: 16px;
        font-weight: 600;
        line-height: 24px;
    }

    .exclamation {
        width: 24px;
        height: 24px;
        flex-shrink: 0;
    }

    .fraud-box {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        padding: 10px;
        gap: 10px;
        border-radius: 3px;
        border: 1px solid $fraud-border;
        background: $fraud-background;
    }

    .pay_monthly-app-number {
        font-size: 26px;
        font-weight: 700;
    }

    .fraud-box-inside {
        display: flex;
        width: 724px;
        flex-direction: column;
        justify-content: center;
        align-items: self-start;
    }
</style>
